import { useCallback, useMemo } from 'react';
import { useAppStore } from '../stores/appStore';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';
import { MAX_CHAT_ATTACHMENTS, createPendingAttachment } from '../utils/chatUploads';

export function useFileAttachment() {
  const activeSessionKey = useAppStore((state) => state.activeSessionKey);
  const pendingAttachments = useAppStore((state) => state.pendingAttachments);
  const sessionImageCountMap = useAppStore((state) => state.sessionImageCount);
  const addPendingAttachment = useAppStore((state) => state.addPendingAttachment);

  const sessionSentCount = activeSessionKey ? (sessionImageCountMap[activeSessionKey] ?? 0) : 0;

  const canAddMore = useMemo(
    () => sessionSentCount + pendingAttachments.length < MAX_CHAT_ATTACHMENTS,
    [pendingAttachments.length, sessionSentCount],
  );

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileList = Array.from(files);
      if (fileList.length === 0) {
        return;
      }

      let totalCount = sessionSentCount + pendingAttachments.length;
      if (totalCount >= MAX_CHAT_ATTACHMENTS) {
        window.alert(`You can send up to ${MAX_CHAT_ATTACHMENTS} files in this chat.`);
        return;
      }

      for (const file of fileList) {
        if (totalCount >= MAX_CHAT_ATTACHMENTS) {
          window.alert(`You can send up to ${MAX_CHAT_ATTACHMENTS} files in this chat.`);
          break;
        }

        try {
          const attachment = await createPendingAttachment(file);
          addPendingAttachment(attachment);
          totalCount += 1;
        } catch (error) {
          console.error('[useFileAttachment] Failed to prepare file for upload:', error);
          window.alert(toFriendlyErrorMessage(error, 'image-upload'));
        }
      }
    },
    [addPendingAttachment, pendingAttachments.length, sessionSentCount],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }

      const files: File[] = [];
      for (const item of items) {
        if (item.kind !== 'file') {
          continue;
        }

        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }

      if (files.length > 0) {
        event.preventDefault();
        void handleFiles(files);
      }
    },
    [handleFiles],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) {
        return;
      }
      void handleFiles(files);
    },
    [handleFiles],
  );

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
  }, []);

  return {
    handleFiles,
    handlePaste,
    handleDrop,
    handleDragOver,
    canAddMore,
  };
}
